<template>
  <div>
    <d-form>
      <d-form-row class="form-group">
        <d-col cols="12" md="8">
          <label>{{ $t('loop.form.name.label') }}</label>
          <d-form-input
            ref="nameInput"
            type="text"
            v-model="model.name"
            :state="hasErrorFiled('name')"
            :placeholder="$t('loop.form.name.placeholder')"
          />
          <d-form-invalid-feedback>
            {{ $t('loop.form.name.invalid') }}
          </d-form-invalid-feedback>
        </d-col>
        <d-col cols="12" md="4" class="d-flex align-items-end justify-content-around">
          <d-checkbox toggle v-model="model.settings.showAnswer" class="custom-toggle-sm">
            <span>{{ $t('loop.form.showAnswer.label') }}</span>
          </d-checkbox>
          <d-checkbox toggle v-model="model.settings.colored" class="custom-toggle-sm">
            <span>{{ $t('loop.form.colored.label') }}</span>
          </d-checkbox>
        </d-col>
      </d-form-row>

      <d-form-row class="form-group align-items-end mt-3 mb-0">
        <d-col cols="12" md="1">
          <label>{{ $t('loop.form.columns.label') }}</label>
          <d-form-input type="number" min="4" v-model.number="model.columns" @input="updateTable" />
        </d-col>
        <d-col cols="12" md="1">
          <label>{{ $t('loop.form.rows.label') }}</label>
          <d-form-input type="number" min="4" v-model.number="model.rows" @input="updateTable" />
        </d-col>
        <d-col cols="12" md="4">
          <div class="d-flex justify-content-between align-items-center mb-1">
            <label class="mb-0">
              {{ $t('loop.form.numObstacles.label') }}
              <i id="tooltip-pairs-style" class="fas fa-info-circle text-muted ml-1" style="cursor: pointer" />
              <d-tooltip target="tooltip-pairs-style" placement="top">
                {{ autoNumberObstaclesMessage }}
              </d-tooltip>
            </label>

            <d-checkbox toggle v-model="model.settings.autoNumberObstacles" class="custom-toggle-sm">
              <small :class="model.settings.autoNumberObstacles ? 'text-primary' : 'text-muted'">
                {{ $t('loop.form.numObstacles.auto') }}
              </small>
            </d-checkbox>
          </div>

          <d-form-input
            type="number"
            :min="1"
            :disabled="model.settings.autoNumberObstacles"
            v-model.number="model.settings.numObstacles"
            :state="isParityIncorrect"
          />
        </d-col>

        <d-col cols="12" md="2">
          <label>{{ $t('loop.form.pathWeight.label') }}</label>
          <d-select v-model="model.settings.pathWeight" class="form-control">
            <option value="thin">
              {{ $t('loop.form.pathWeight.thin') }}
            </option>
            <option value="normal">
              {{ $t('loop.form.pathWeight.normal') }}
            </option>
            <option value="bold">
              {{ $t('loop.form.pathWeight.bold') }}
            </option>
          </d-select>
        </d-col>

        <d-col cols="12" md="2">
          <div class="d-flex flex-column mb-1">
            <label class="mb-2">
              {{ $t('loop.form.prioritizeQuality.label') }}
              <i id="tooltip-quality" class="fas fa-info-circle text-muted ml-1" style="cursor: pointer" />
              <d-tooltip target="tooltip-quality" placement="top">
                {{ $t('loop.form.prioritizeQuality.tooltip') }}
              </d-tooltip>
            </label>
            <d-checkbox toggle v-model="model.settings.prioritizeQuality" class="custom-toggle-sm">
              <small :class="model.settings.prioritizeQuality ? 'text-primary' : 'text-muted'">
                {{ $t('loop.form.prioritizeQuality.enabled') }}
              </small>
            </d-checkbox>
          </div>
        </d-col>

        <d-col cols="12" md="2">
          <div class="d-flex flex-column mb-1">
            <label class="mb-2">
              {{ $t('loop.form.fastGeneration.label') }}
              <i id="tooltip-fast" class="fas fa-info-circle text-muted ml-1" style="cursor: pointer" />
              <d-tooltip target="tooltip-fast" placement="top">
                {{ fastGenerationTooltip }}
              </d-tooltip>
            </label>

            <d-checkbox
              toggle
              v-model="model.settings.fastGeneration"
              :disabled="isLargeGrid"
              class="custom-toggle-sm"
            >
              <small :class="model.settings.fastGeneration ? 'text-primary' : 'text-muted'">
                {{ $t('loop.form.fastGeneration.enabled') }}
              </small>
            </d-checkbox>
          </div>
        </d-col>
      </d-form-row>
      <d-form-row class="form-group align-items-end mt-0">
        <d-col cols="12" md="2" />
        <d-col cols="12" md="4">
          <div v-if="isParityIncorrect === false" class="text-danger small mt-1">
            <i class="fas fa-exclamation-triangle"></i>
            {{
              $t('loop.form.numObstacles.parityHint', {
                parity: expectedParity,
              })
            }}
          </div>
        </d-col>
      </d-form-row>
    </d-form>

    <div class="position-relative">
      <board-grid
        v-if="model.table.length"
        :table="model.table"
        :rows="model.rows"
        :columns="model.columns"
        :cell-size="cellSize"
        :settings="model.settings"
      />
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import mixin from '_common/mixins/ValidateForm';
import BoardGrid from '_common/components/board/BoardGrid.vue';
import { cloneDeep } from '_utils';
import Events from '_utils/events';
import FormatErrors from '_common/mixins/FormatErrors';
import workerManager from '_common/mixins/workerManager';
import PuzzleService from '_services/rest/modules/puzzle';
import createLoopWorker from '_utils/puzzleLoop/createWorker';
import { _LOAD_PUZZLE_LOOP_MODEL_, CELL, CELL_SIZE, MODEL } from './PuzzleLoopModel';

export default {
  name: 'puzzle-loop-form',
  components: { BoardGrid },
  mixins: [mixin, FormatErrors, workerManager],
  data() {
    return {
      model: cloneDeep(MODEL),
      errors: { name: { required: false } },
      cellSize: CELL_SIZE,
      isGenerating: false,
      workersFinished: 0,
      globalBestScore: -Infinity,
    };
  },
  computed: {
    autoNumberObstaclesMessage() {
      if (this.model.settings.autoNumberObstacles) {
        return this.$t('loop.form.numObstacles.tooltipAuto', {
          count: this.model.settings.numObstacles,
        });
      }
      return this.$t('loop.form.numObstacles.tooltipManual');
    },
    fastGenerationTooltip() {
      if (this.isLargeGrid) {
        return this.$t('loop.form.fastGeneration.tooltipLarge');
      }
      return this.$t('loop.form.fastGeneration.tooltip');
    },
    isParityIncorrect() {
      const area = this.model.rows * this.model.columns;
      const num = this.model.settings.numObstacles;

      const remainingCells = area - num;
      const isOk = remainingCells % 2 === 0;

      return isOk ? null : false;
    },
    expectedParity() {
      const area = this.model.rows * this.model.columns;
      return area % 2 !== 0
        ? this.$t('loop.form.numObstacles.parityOdd')
        : this.$t('loop.form.numObstacles.parityEven');
    },
    isLargeGrid() {
      return Math.max(this.model.rows, this.model.columns) >= 9;
    },
  },
  methods: {
    ...mapActions(['activeAlert', 'setHasChanges', 'setPuzzleLoop']),
    resolveWorkerErrorMessage(data) {
      if (data.errorCode) {
        return this.$t(`loop.alert.workerErrors.${data.errorCode}`);
      }

      return data.message || this.$t('loop.alert.generateFailed');
    },
    updateTable() {
      const { rows, columns } = this.model;
      const newTable = [];
      for (let y = 0; y < rows; y += 1) {
        for (let x = 0; x < columns; x += 1) {
          const existing = this.model.table.find(
            c => Number(c.positionx) === x && Number(c.positiony) === y,
          );
          newTable.push(
            existing || {
              ...CELL,
              id: y * columns + x,
              positionx: x,
              positiony: y,
            },
          );
        }
      }
      this.model.table = newTable;
    },
    generate() {
      this.cancelPreviousGeneration();
      this.startGenerationLoader();

      this.isGenerating = true;
      this.globalBestScore = -Infinity;
      this.workersFinished = 0;

      const workerCount = this.resolveWorkerCount();
      const targetObstacles = this.resolveNumberObstacles();
      const perfectScore = targetObstacles * 4;

      for (let i = 0; i < workerCount; i += 1) {
        const worker = createLoopWorker();
        this.workers.push(worker);

        worker.postMessage({
          rows: this.model.rows,
          columns: this.model.columns,
          strategy: i,
          numObstacles: targetObstacles,
          fastGeneration: this.model.settings.fastGeneration,
        });

        worker.onmessage = ({ data }) => {
          if (!this.isGenerating) return;

          if (data.status === 'update') {
            if (!this.model.settings.prioritizeQuality) {
              this.applyResultToTable(data.puzzle);
              this.finishGeneration(
                this.$t('loop.alert.firstLoopFound'),
                'success',
              );
              return;
            }

            if (data.score > this.globalBestScore) {
              this.globalBestScore = data.score;
              this.applyResultToTable(data.puzzle);

              if (data.score >= perfectScore) {
                this.finishGeneration(
                  this.$t('loop.alert.perfectLoopFound'),
                  'success',
                );
              }
            }
          } else if (data.status === 'finished') {
            this.workersFinished += 1;

            if (this.workersFinished === workerCount && this.isGenerating) {
              if (this.globalBestScore > -Infinity) {
                this.finishGeneration(
                  this.$t('loop.alert.bestOptionFound'),
                  'success',
                );
              } else {
                this.finishGeneration(
                  this.$t('loop.alert.generateFailed'),
                  'warning',
                );
              }
            }
          } else if (data.status === 'error') {
            this.activeAlert({
              message: this.$t('loop.alert.workerError', {
                index: i,
                message: this.resolveWorkerErrorMessage(data),
              }),
              theme: 'danger',
            });
            this.cancelPreviousGeneration();
          }
        };

        worker.onerror = () => {
          this.activeAlert({
            message: this.$t('loop.alert.workerCritical'),
            theme: 'danger',
          });
          this.cancelPreviousGeneration();
        };
      }
    },
    resolveWorkerCount() {
      return navigator.hardwareConcurrency
        ? Math.min(navigator.hardwareConcurrency, 4)
        : 2;
    },
    finishGeneration(msg, theme = 'success') {
      this.isGenerating = false;
      this.cleanupWorkers();
      this.activeAlert({ message: msg, theme });
      this.setHasChanges(true);
    },
    applyResultToTable(puzzle) {
      const { path, obstacles } = puzzle;
      const pathMap = new Map(path.map((p, i) => [`${p.x},${p.y}`, i]));
      const obstacleSet = new Set(obstacles.map(o => `${o.x},${o.y}`));

      this.model.table.forEach(cell => {
        const key = `${cell.positionx},${cell.positiony}`;
        const newCell = cell;

        newCell.isBlocked = obstacleSet.has(key);
        newCell.direction = null;

        if (pathMap.has(key)) {
          const i = pathMap.get(key);
          const current = path[i];
          const prev = path[(i - 1 + path.length) % path.length];
          const next = path[(i + 1) % path.length];

          newCell.direction = this.getDirectionKey(current, prev, next);
          newCell.color = '#ff2c2c';
        } else {
          newCell.color = '';
        }
      });
    },
    getDirectionKey(curr, p, n) {
      const dPrevX = p.x - curr.x;
      const dPrevY = p.y - curr.y;
      const dNextX = n.x - curr.x;
      const dNextY = n.y - curr.y;

      const hasTop = dPrevY === -1 || dNextY === -1;
      const hasBottom = dPrevY === 1 || dNextY === 1;
      const hasLeft = dPrevX === -1 || dNextX === -1;
      const hasRight = dPrevX === 1 || dNextX === 1;

      if (hasLeft && hasRight) return 'h';
      if (hasTop && hasBottom) return 'v';
      if (hasTop && hasRight) return 'tr';
      if (hasTop && hasLeft) return 'tl';
      if (hasBottom && hasRight) return 'br';
      if (hasBottom && hasLeft) return 'bl';

      return 'h';
    },
    resolveNumberObstacles() {
      const area = this.model.rows * this.model.columns;
      const isAreaOdd = area % 2 !== 0;
      let num = parseInt(this.model.settings.numObstacles, 10) || 2;

      if (this.model.settings.autoNumberObstacles) {
        num = Math.floor(area * 0.25);

        if (isAreaOdd) {
          if (num % 2 === 0) num += 1;
        } else if (num % 2 !== 0) num += 1;

        if (num < 4 && area >= 16) {
          num = 4;
        }

        this.model.settings.numObstacles = num;
      }

      return this.model.settings.numObstacles;
    },
    onAutoNumberObstaclesChange(isAuto) {
      if (isAuto) {
        this.resolveNumberObstacles();
      }
    },
    save() {
      if (this.checkForm()) {
        const puzzle = {
          ...this.model,
          slug: `${this.model.name.replace(/\s+/g, '-')}-puzzle-loop`,
        };
        PuzzleService.save(puzzle)
          .then(response => {
            this.activeAlert({
              message: this.$t('loop.alert.gameSaved'),
              theme: 'success',
            });
            this.setPuzzleLoop(response);
            if (this.$route.name === 'admin-puzzle-loop-new') {
              this.$router.push({
                name: 'admin-puzzle-loop-edit',
                params: { puzzleId: response.id },
              });
            }
            this.setHasChanges(false);
          })
          .catch(error => {
            const errors = this.formatErrors(error.data);
            this.activeAlert({ message: errors, theme: 'danger' });
          });
      }
    },
  },
  watch: {
    'model.settings.autoNumberObstacles': 'onAutoNumberObstaclesChange',
    'model.rows': 'resolveNumberObstacles',
    'model.columns': 'resolveNumberObstacles',

    isLargeGrid: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.model.settings.fastGeneration = true;
        }
      },
    },
  },
  mounted() {
    this.resolveNumberObstacles();
    Events.$on(_LOAD_PUZZLE_LOOP_MODEL_, model => {
      this.model = { ...model };
    });
  },
  beforeDestroy() {
    Events.$off(_LOAD_PUZZLE_LOOP_MODEL_);
  },
};
</script>
